import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import Markdown from '../Markdown'
import { bodyText, colours } from '../player/playerStyles'
import useBlockState from './useBlockState'

// Areas to find on a picture. Every area is also listed as an ordinary
// button under the picture, so the activity works without seeing the image
// or being able to hit a small target.
const Hotspot = ({ block, content, initialState, onStateChange }) => {
  const [state, update] = useBlockState({ found: [], ...initialState }, onStateChange)
  const found = state.found ?? []
  const [selectedId, setSelectedId] = useState(null)
  const selected = block.spots.find((spot) => spot.id === selectedId)
  const src = content?.media?.[block.media]

  const select = (spotId) => {
    setSelectedId(spotId)
    if (!found.includes(spotId)) update({ ...state, found: [...found, spotId] })
  }

  return (
    <Box>
      <Markdown sx={{ ...bodyText, mb: 2 }}>{block.intro}</Markdown>

      {src && (
        <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%', lineHeight: 0 }}>
          <Box component="img" src={src} alt={block.alt} sx={{ display: 'block', maxWidth: '100%', height: 'auto', borderRadius: 1 }} />
          {block.spots.map((spot, index) => {
            const visited = found.includes(spot.id)
            return (
              <ButtonBase
                key={spot.id}
                onClick={() => select(spot.id)}
                aria-label={visited ? `${spot.label} (opened)` : spot.label}
                aria-pressed={selectedId === spot.id}
                sx={{
                  position: 'absolute',
                  left: `${spot.x}%`,
                  top: `${spot.y}%`,
                  width: `${spot.r * 2}%`,
                  minWidth: 44,
                  minHeight: 44,
                  aspectRatio: '1 / 1',
                  transform: 'translate(-50%, -50%)',
                  borderRadius: '50%',
                  border: `3px ${visited ? 'solid' : 'dashed'} ${visited ? colours.good : colours.accent}`,
                  bgcolor: visited ? 'rgba(236, 253, 245, 0.85)' : 'rgba(255, 255, 255, 0.7)',
                  color: visited ? colours.good : colours.accent,
                  fontSize: '1rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  boxShadow: selectedId === spot.id ? `0 0 0 3px #FFFFFF, 0 0 0 6px ${colours.accent}` : 'none',
                }}
              >
                {visited ? <CheckIcon aria-hidden="true" /> : index + 1}
              </ButtonBase>
            )
          })}
        </Box>
      )}

      <Typography aria-live="polite" sx={{ fontSize: '1rem', fontWeight: 700, color: colours.muted, mt: 2, mb: 1 }}>
        {found.length} of {block.spots.length} found
      </Typography>

      <Box
        aria-live="polite"
        sx={{ minHeight: 72, p: selected ? 2 : 0, mb: 2, borderRadius: 1, bgcolor: selected ? colours.accentSoft : 'transparent', borderLeft: selected ? `4px solid ${colours.accent}` : 'none' }}
      >
        {selected ? (
          <>
            <Typography component="h3" sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 0.5 }}>
              {selected.label}
            </Typography>
            <Markdown sx={bodyText}>{selected.body}</Markdown>
          </>
        ) : (
          <Typography sx={{ ...bodyText, color: colours.muted }}>
            Choose an area on the picture, or from the list below, to read about it.
          </Typography>
        )}
      </Box>

      <Typography component="h3" sx={{ fontSize: '1.0625rem', fontWeight: 700, mb: 1 }}>
        Areas to find
      </Typography>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {block.spots.map((spot, index) => {
          const visited = found.includes(spot.id)
          return (
            <li key={spot.id}>
              <Button
                variant={selectedId === spot.id ? 'contained' : 'outlined'}
                onClick={() => select(spot.id)}
                aria-pressed={selectedId === spot.id}
                startIcon={visited ? <CheckIcon /> : undefined}
                sx={{ textAlign: 'left' }}
              >
                {index + 1}. {spot.label}
                {visited ? ' (opened)' : ''}
              </Button>
            </li>
          )
        })}
      </Box>
    </Box>
  )
}

export default Hotspot
